import React, { useEffect, useState, useMemo } from 'react'
import { FaAngleLeft } from 'react-icons/fa6';
import { MdOutlineDateRange } from "react-icons/md";
import { BsDownload } from "react-icons/bs";
import { 
    FiArrowUpRight, 
    FiArrowDownLeft, 
    FiRefreshCw, 
    FiPlus, 
    FiCreditCard, 
    FiTrash2, 
    FiPlusCircle, 
    FiMinusCircle, 
    FiSearch 
} from "react-icons/fi";
import { SiConvertio } from 'react-icons/si';

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import '../Transactions/transactions.css';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
    getAllVirtualCardActivities, 
    verifyVirtualCardActivity,
    getVirtualCards,
    getVirtualCardActivities 
} from '../../../api/api';
import Pagination from '../../../components/Pagination/Pagination';

const typeIcons = {
    credit: <FiArrowDownLeft size={18} />,
    debit: <FiArrowUpRight size={18} />,
    swap: <SiConvertio size={18} />,
    fund: <FiPlusCircle size={18} />,
    creation: <FiCreditCard size={18} />,
    withdraw: <FiMinusCircle size={18} />,
    termination: <FiTrash2 size={18} />
};

const getTypeIcon = (purpose, type, operationType) => {
    const key = (purpose || type || operationType || '').toLowerCase();
    if (key.includes('fund') || key.includes('credit')) return typeIcons.fund;
    if (key.includes('withdraw')) return typeIcons.withdraw;
    if (key.includes('creation') || key.includes('create')) return typeIcons.creation;
    if (key.includes('termination') || key.includes('terminate')) return typeIcons.termination;
    if (key.includes('swap')) return typeIcons.swap;
    if (key.includes('debit')) return typeIcons.debit;
    return typeIcons.credit;
};

const ActivityLogs = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    const [activities, setActivities] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [loading, setLoading] = useState(false);
    const [verifyingId, setVerifyingId] = useState(null);
    const itemsPerPage = 10;

    // Card selection filters
    const [cardsList, setCardsList] = useState([]);
    const [selectedCardId, setSelectedCardId] = useState(location.state?.cardId || '');

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;

    // Fetch list of cards for selector dropdown
    useEffect(() => {
        const fetchCards = async () => {
            try {
                const response = await getVirtualCards();
                let cards = [];
                if (Array.isArray(response.data)) {
                  cards = response.data;
                } else if (Array.isArray(response.data?.data)) {
                  cards = response.data.data;
                } else if (Array.isArray(response.data?.data?.data)) {
                  cards = response.data.data.data;
                } else if (Array.isArray(response.data?.data?.data?.data)) {
                  cards = response.data.data.data.data;
                }
                
                setCardsList(cards);
                if (cards.length > 0 && !selectedCardId) {
                    setSelectedCardId(cards[0].id);
                }
            } catch (error) {
                console.error("Failed to fetch cards for selector", error);
            }
        };
        fetchCards();
    }, []);

    const fetchActivities = async (page) => {
        if (!selectedCardId) return; // Wait until a card is selected
        setLoading(true);
        try {
            const response = await getVirtualCardActivities(selectedCardId, { page, per_page: itemsPerPage });
            const { data, current_page, total_pages } = response.data?.data || {};
            setActivities(data || []);
            setCurrentPage(current_page || 1);
            setTotalPages(total_pages || 1);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchActivities(currentPage);
    }, [currentPage, selectedCardId]);

    const handleVerify = async (activityId) => {
        setVerifyingId(activityId);
        try {
            await verifyVirtualCardActivity(activityId);
            fetchActivities(currentPage);
        } catch (error) {
            console.error("Failed to verify activity", error);
        } finally {
            setVerifyingId(null);
        }
    };

    const handleGoBack = () => {
        navigate(`/user/cards`); 
    };

    const goToFundCard = () => {
        if (selectedCardId) {
            navigate(`/user/cards/card_option/${selectedCardId}`); 
        } else {
            navigate(`/user/cards`);
        }
    };

    // Client-side search and date filtering
    const groupedActivities = useMemo(() => {
        if (!activities) return {};
        
        let filtered = [...activities];
        
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(act => {
                const title = (act.title || act.purpose || act.type || act.operation_type || '').toLowerCase();
                const desc = (act.description || act.merchant_name || act.merchant_category_code || act.gateway_reference || '').toLowerCase();
                const amountStr = String(act.amount || '').toLowerCase();
                const currencyStr = String(act.currency || '').toLowerCase();
                return title.includes(query) || desc.includes(query) || amountStr.includes(query) || currencyStr.includes(query);
            });
        }
        
        if (startDate && endDate) {
            filtered = filtered.filter(act => {
                if (!act.created_at) return false;
                const actDate = new Date(act.created_at);
                const start = new Date(startDate);
                start.setHours(0, 0, 0, 0);
                const end = new Date(endDate);
                end.setHours(23, 59, 59, 999);
                return actDate >= start && actDate <= end;
            });
        }

        return filtered.reduce((groups, act) => {
            if (!act.created_at) return groups;
            const date = new Date(act.created_at).toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });
            
            const today = new Date().toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });

            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            const yesterdayStr = yesterday.toLocaleDateString("en-US", {
                day: "numeric",
                month: "long",
                year: "numeric"
            });

            let groupName = date;
            if (date === today) groupName = "Today";
            else if (date === yesterdayStr) groupName = "Yesterday";

            if (!groups[groupName]) groups[groupName] = [];
            groups[groupName].push(act);
            return groups;
        }, {});
    }, [activities, searchQuery, startDate, endDate]);

    // Handle statement download to CSV
    const handleDownloadCSV = () => {
        if (!activities || activities.length === 0) {
            window.alert("No activity logs to download");
            return;
        }
        
        const headers = ["Date", "Title", "Description", "Status", "Amount", "Currency"];
        const rows = activities.map(act => [
            act.created_at ? new Date(act.created_at).toLocaleString() : '',
            act.title || act.purpose || act.type || act.operation_type || '',
            act.description || act.merchant_name || act.merchant_category_code || act.gateway_reference || '',
            act.status || '',
            act.amount || '',
            act.currency || ''
        ]);
        
        const csvContent = "data:text/csv;charset=utf-8," 
            + [headers.join(","), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(","))].join("\n");
            
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `card_activity_logs_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <section className='main p-2 transactions__page pt-4'>
            <div className="container-fluid p-0" >
                <div className="d-flex align-items-center mb-4">
                    <button onClick={handleGoBack} className="back-btn-modern">
                        <FaAngleLeft size={16} />
                        <span>Back to Card Options</span>
                    </button>
                </div>

                <main>
                    <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
                        <h4 className="mb-0 fw-semibold text-dark">Activity Logs</h4>
                        <button onClick={goToFundCard} className='add_fund-action'>
                            <FiPlus size={18} />
                            <span>Fund Card</span>
                        </button>
                    </div>

                    <div className="mt-3">
                        <div className="transaction-filter d-flex align-items-center justify-content-between flex-wrap gap-3">
                            <div className="search-box">
                                <FiSearch className="search-icon" />
                                <input 
                                    type="text" 
                                    className="form-control" 
                                    placeholder="Search details or amount..." 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                />
                            </div>

                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                <div className="d-flex align-items-center gap-2 bg-white px-2 py-1 shadow-sm" style={{ borderRadius: '8px', border: '1px solid #E0E4EC' }}>
                                    <span className="text-muted fw-medium small ps-2" style={{ whiteSpace: 'nowrap', fontSize: '12px' }}>Card:</span>
                                    <select 
                                        className="form-select border-0 shadow-none" 
                                        style={{ fontSize: '13px', color: '#19064B', fontWeight: '500', minWidth: '160px', backgroundColor: 'transparent' }}
                                        value={selectedCardId}
                                        onChange={(e) => {
                                            setSelectedCardId(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    >
                                        {cardsList.map(c => (
                                            <option key={c.id} value={c.id}>
                                                {c.nickname || c.card_name || c.masked_pan || 'Virtual Card'}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="date-picker-wrapper">
                                    <MdOutlineDateRange size={20} color="#94A3B8"/>
                                    <div className="date-picker">
                                        <DatePicker
                                            selectsRange
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={(update) => setDateRange(update || [null, null])}
                                            isClearable
                                            placeholderText="Select date range"
                                            className="form-control"
                                        />
                                    </div>
                                </div>

                                <button 
                                    onClick={handleDownloadCSV} 
                                    className="download-action"
                                    title="Download CSV"
                                >
                                    <BsDownload size={18} />
                                </button>
                            </div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="text-center my-5 py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : (
                        <div className="txn-list-container mt-4">
                            {Object.keys(groupedActivities).length > 0 ? (
                                Object.entries(groupedActivities).map(([date, items]) => (
                                    <div key={date} className="txn-list-group">
                                        <h2 className="txn-date-header">{date}</h2>
                                        {items.map((transaction) => {
                                            const opType = (transaction.operation_type || transaction.type || transaction.purpose || '').toLowerCase();
                                            const isCredit = opType === 'credit' || opType === 'fund';
                                            const isDebit = opType === 'debit' || opType === 'withdraw' || opType === 'termination' || opType === 'create' || opType === 'creation';
                                            const iconTypeClass = isCredit ? 'credit' : isDebit ? 'debit' : 'pending';
                                            const status = transaction.status;
                                            
                                            return (
                                                <div key={transaction.id || transaction.ref_id} className="txn-item">
                                                    <div className="txn-item-left">
                                                        <div className={`txn-icon-box ${iconTypeClass}`}>
                                                            {getTypeIcon(transaction.purpose, transaction.type, transaction.operation_type)}
                                                        </div>
                                                        <div className="txn-main-info">
                                                            <h3 className="text-capitalize">
                                                                {transaction.title || transaction.purpose || transaction.type || transaction.operation_type}
                                                            </h3>
                                                            <p>
                                                                {transaction.description || transaction.merchant_name || transaction.merchant_category_code || transaction.gateway_reference || 'Card Transaction'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    
                                                    <div className="txn-item-right d-flex align-items-center gap-3">
                                                        <div className="d-flex flex-column align-items-end">
                                                            <div className={`txn-amount ${status === 'failed' ? 'failed-date' : iconTypeClass} `} >
                                                                {transaction.created_at ? new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true }).format(new Date(transaction.created_at)).toUpperCase() : ''}
                                                            </div>
                                                            <span className={`txn-status-badge status-${status === 'completed' || status === 'success' ? 'completed' : status === 'failed' ? 'failed' : 'pending'}`}>
                                                                {status}
                                                            </span>
                                                        </div>
                                                        
                                                        {(status === 'pending' || status === 'processing') && (
                                                            <button 
                                                                className="btn btn-sm btn-outline-primary txn-verify-btn"
                                                                onClick={() => handleVerify(transaction.id || transaction.ref_id)}
                                                                disabled={verifyingId === (transaction.id || transaction.ref_id)}
                                                            >
                                                                {verifyingId === (transaction.id || transaction.ref_id) ? (
                                                                    <>
                                                                        <FiRefreshCw className="refresh-spin-icon" size={12} />
                                                                        <span>Refreshing...</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FiRefreshCw size={12} />
                                                                        <span>Refresh</span>
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ))
                            ) : (
                                <div className="text-center py-5">
                                    <h4 className="text-muted">No activities found</h4>
                                    <p className="text-muted small">Try adjusting your filters or date range</p>
                                </div>
                            )}

                            {activities?.length > 0 && totalPages > 1 && (
                                <div className="mt-4">
                                    <Pagination
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        onPageChange={(pageNum) => setCurrentPage(pageNum)}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </section>
    );
};

export default ActivityLogs;